import { ElementType, ReactElement } from "react";

import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { UserAnalyticsService } from "@analytics/index";

import NewChip from "../NewChip";

export interface IBreadcrumb {
  beta?: boolean;
  clickable: boolean;
  icon: ElementType;
  label: string;
  onClick: () => void;
  tooltipText: ReactElement | string;
}

export interface IBreadcrumbsStepperProperties {
  breadcrumbs: IBreadcrumb[];
  handleClickIndex?: (index: number) => void;
  index: number | undefined;
  workflow: string;
}

/**
 * A breadcrumb stepper with icons.
 * @param index - Current step index.
 * @param breadcrumbs - Array of breadcrumb objects.
 * @param handleClickIndex - Function to update the slide index.
 * @param workflow - The current workflow.
 */
function BreadcrumbsStepper({
  breadcrumbs = [],
  handleClickIndex,
  index = 0,
  workflow,
}: IBreadcrumbsStepperProperties) {
  const theme = useTheme();
  return (
    <div role="presentation">
      <Breadcrumbs aria-label="breadcrumb" separator={<KeyboardArrowRightRoundedIcon />}>
        {breadcrumbs.map((step, pos) => {
          const Icon = step.icon;
          const isActive = index === pos;
          const isCompleted = index > pos;

          let textColor = theme.palette.text.secondary;
          let fontWeight = "400";

          if (isActive) {
            textColor = theme.palette.primary.dark;
            fontWeight = "700";
          } else if (isCompleted) {
            textColor = theme.palette.secondary.main;
            fontWeight = "600";
          }

          if (!step.clickable && !isActive) {
            return (
              <Tooltip key={step.label} title={step.tooltipText}>
                <span style={{ cursor: "not-allowed" }}>
                  <Icon
                    fontSize="inherit"
                    sx={{
                      color: textColor,
                      fontWeight,
                      mr: 0.5,
                    }}
                  />
                  {step.label}
                </span>
              </Tooltip>
            );
          }

          function handleClick() {
            if (handleClickIndex) {
              UserAnalyticsService.trackBreadcrumbsNavigation(
                workflow,
                breadcrumbs[index]?.label ?? "",
                breadcrumbs[pos]?.label ?? "",
              );
              handleClickIndex(pos);
            } else {
              step.onClick();
            }
          }

          return (
            <Box
              key={step.label}
              onClick={handleClick}
              sx={{
                alignItems: "center",
                cursor: "pointer",
                display: "flex",
              }}
            >
              <Tooltip key={step.label} title={step.tooltipText}>
                <Box
                  sx={{
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    alignItems: "center",
                    color: textColor,
                    cursor: "pointer",
                    display: "flex",
                    fontWeight,
                    textDecoration: "none",
                  }}
                >
                  <Icon fontSize="16px" sx={{ color: textColor, mr: 0.5 }} />
                  <Typography fontWeight="600" variant="body1">
                    {step.label}
                  </Typography>
                </Box>
              </Tooltip>
              {step.beta && <NewChip isActive={isActive} />}
            </Box>
          );
        })}
      </Breadcrumbs>
    </div>
  );
}

export default BreadcrumbsStepper;
